import responsiveImages from "./responsiveImages.json";
import React from "react";
import {
  Box,
  Typography,
  Grid,
  Fade,
  Container,
  Chip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { ArrowForward } from "@mui/icons-material";

const COLORS = {
  primary: "#123465",
  secondary: "#f9a836",
  white: "#fff",
  lightBg: "#f4f8fc",
};

const servicesData = [
  {
    title: "Cybersécurité",
    description:
      "Solutions de protection robustes pour sécuriser vos réseaux, données et terminaux contre les menaces modernes.",
    accent: "#e53935",
    image: "/images/Service/4.webp",
    link: "/services/cybersecurite",
    features: ["Audit & gouvernance", "Protection endpoints", "Supervision SOC"],
    badge: "Nouveau",
  },
  {
    title: "Impression managée",
    description:
      "Optimisez et automatisez votre parc d'imprimantes et vos processus d'étiquetage pour plus d'efficacité.",
    accent: "#2e7d32",
    image: "/images/Service/3.webp",
    link: "/services/impression-managee",
    features: ["Parc & consommables", "SLA maintenance", "Étiquetage industriel"],
  },
  {
    title: "Gestion MDM",
    description:
      "Déployez, gérez et sécurisez votre flotte d'appareils mobiles (EMM/MDM) avec nos solutions leaders.",
    accent: "#1565c0",
    image: "/images/Service/6.webp",
    link: "/services/gestion-mdm",
    features: ["Enroll & policies", "Android Enterprise", "App management"],
  },
  {
    title: "Développement d'applications",
    description:
      "Conception d'applications web et mobiles sur mesure : UX, API, intégrations et déploiement.",
    accent: "#00838f",
    image: "/images/Service/2.webp",
    link: "/services/developpement-applications",
    features: ["Web & Mobile", "API & intégrations", "Maintenance & évolution"],
  },
  {
    title: "Conseil & support IT",
    description:
      "Notre équipe d'experts vous accompagne dans vos projets IT et assure un support technique réactif.",
    accent: "#e65100",
    image: "/images/Service/5.webp",
    link: "/services/conseil-support-it",
    features: ["Pilotage projets", "Support N1/N2", "Interventions terrain"],
  },
  {
    title: "Gestion des inventaires",
    description:
      "Suivi et gestion de vos stocks et immobilisations avec nos solutions logicielles et matérielles dédiées.",
    accent: "#6a1b9a",
    image: "/images/Service/1.webp",
    link: "/services/gestion-des-inventaires",
    features: ["Stock & immos", "RFID / codes-barres", "Reporting & audit"],
  },
];

export default function Services() {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 11 },
        bgcolor: COLORS.lightBg,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.76) 0%, rgba(255,255,255,0) 24%, rgba(255,255,255,0.55) 100%)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: -120,
          right: -100,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(18,52,101,0.08) 0%, transparent 72%)",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Fade in appear={false} timeout={550}>
          <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: "center" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "rgba(255,255,255,0.86)",
                border: "1px solid rgba(18,52,101,0.10)",
                borderRadius: 999,
                px: 2,
                py: 0.6,
                mb: 2.2,
                boxShadow: "0 10px 24px rgba(18,52,101,0.06)",
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: COLORS.secondary }} />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: COLORS.secondary,
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                }}
              >
                Expertise & accompagnement
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2.5, md: 4 }} alignItems="end" justifyContent="center">
              <Grid item xs={12} md={12}>
                <Typography
                  variant="h2"
                  sx={{
                    color: COLORS.primary,
                    fontWeight: 900,
                    fontSize: { xs: "2rem", sm: "2.45rem", md: "3.15rem" },
                    lineHeight: 1.08,
                    letterSpacing: -1.1,
                    maxWidth: 720,
                    mx: "auto",
                  }}
                >
                  Des services pensés pour
                  <Box component="span" sx={{ color: COLORS.secondary, display: "block" }}>
                    fiabiliser vos opérations
                  </Box>
                </Typography>
              </Grid>

              <Grid item xs={12} md={12}>
                <Typography
                  sx={{
                    color: "rgba(15,23,42,0.66)",
                    fontSize: { xs: "0.98rem", md: "1.02rem" },
                    lineHeight: 1.85,
                    fontWeight: 500,
                    maxWidth: 520,
                    mx: "auto",
                  }}
                >
                  Accompagnement terrain, conseil spécialisé et solutions de déploiement
                  pour faire avancer vos projets avec plus de maîtrise, plus de continuité
                  et moins de friction.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Fade>

        <Grid container spacing={{ xs: 2.2, md: 2.8 }}>
          {servicesData.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={service.title}>
              <Fade in appear={false} timeout={500 + index * 100}>
                <Box
                  component={RouterLink}
                  to={service.link}
                  onClick={() => window.scrollTo(0, 0)}
                  sx={{
                    textDecoration: "none",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    borderRadius: "24px",
                    overflow: "hidden",
                    bgcolor: COLORS.white,
                    border: "1px solid rgba(15,23,42,0.08)",
                    boxShadow: "0 16px 36px rgba(15,23,42,0.07)",
                    transition: "transform .28s ease, box-shadow .28s ease, border-color .28s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 26px 56px rgba(15,23,42,0.12)",
                      borderColor: "rgba(18,52,101,0.16)",
                    },
                    "&:hover .svc-img": {
                      transform: "scale(1.06)",
                    },
                    "&:hover .svc-cta": {
                      color: service.accent,
                    },
                    "&:hover .svc-arrow-wrap": {
                      bgcolor: service.accent,
                      color: COLORS.white,
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Box sx={{ position: "relative", height: 220, overflow: "hidden" }}>
                    <Box
                      className="svc-img"
                      component="img"
                      src={service.image}
                      srcSet={responsiveImages[service.image]}
                      sizes="(max-width: 599px) calc(100vw - 32px), (max-width: 899px) calc(50vw - 32px), 380px"
                      decoding="async"
                      alt={service.title}
                      loading="lazy"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform .5s ease",
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(180deg, rgba(5,15,32,0.05) 0%, rgba(5,15,32,0.72) 100%)",
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 3,
                        bgcolor: service.accent,
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        left: 18,
                        right: 18,
                        top: 18,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1.2,
                      }}
                    >

                      {service.badge && (
                        <Chip
                          label={service.badge}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.65rem",
                            bgcolor: COLORS.secondary,
                            color: COLORS.primary,
                            fontWeight: 700,
                            borderRadius: "8px",
                            "& .MuiChip-label": { px: 0.85 },
                          }}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        position: "absolute",
                        left: 18,
                        right: 18,
                        bottom: 18,
                      }}
                    >
                      <Typography
                        sx={{
                          color: COLORS.white,
                          fontWeight: 850,
                          fontSize: "1.2rem",
                          lineHeight: 1.18,
                          textShadow: "0 2px 10px rgba(0,0,0,0.4)",
                          maxWidth: "88%",
                        }}
                      >
                        {service.title}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: { xs: 2.3, md: 2.6 },
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "rgba(15,23,42,0.65)",
                        fontSize: "0.92rem",
                        lineHeight: 1.8,
                        mb: 2.1,
                        fontWeight: 500,
                      }}
                    >
                      {service.description}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.95, mb: 2.6 }}>
                      {service.features.slice(0, 3).map((feature) => (
                        <Box key={feature} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              bgcolor: service.accent,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              color: "rgba(15,23,42,0.74)",
                              fontWeight: 600,
                              fontSize: "0.86rem",
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box
                      sx={{
                        mt: "auto",
                        pt: 1.6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        className="svc-cta"
                        sx={{
                          color: { xs: service.accent, sm: COLORS.primary },
                          fontWeight: 800,
                          fontSize: "0.9rem",
                          transition: "color .2s ease",
                        }}
                      >
                        Découvrir le service
                      </Typography>

                      <Box
                        className="svc-arrow-wrap"
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "14px",
                          bgcolor: {
                            xs: service.accent,
                            sm: "rgba(18,52,101,0.08)",
                          },
                          color: { xs: COLORS.white, sm: COLORS.primary },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          transition: "all .22s ease",
                        }}
                      >
                        <ArrowForward sx={{ fontSize: 18 }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
